<template>
  <shared-product-provide
    v-if="account && Object.keys(account).length > 0"
    :product="product"
    :fromModule="fromModule"
    :toModule="toModule"
  >

    <div class="c-box" v-if="!loading">
      <loader :loading="sending" :label="getCountVariations()"/>

      <header class="c-box__heading" :class="{disabledHeader: product.status === 'cannotSend'}" @click="toggleProductInfo()">
        <span v-once>{{ product.title }}</span>
        <i :class="toggleIconClass()" class="u-floatRight icon"></i>
      </header>

      <div class="c-box__body" v-show="show && !everyVariationWithErrorOrSent">
        <div class="c-box__inner">

          <panel v-if="product" title="Informações gerais do produto">
            <div slot="header-right">
              <button :disabled="addingImage" :class="{ 'is-loading': addingImage }" @click="addingImage = true"
                      class="hint hint--left btn btn--info" data-label="Adicionar uma imagem em uma ou mais variações">
                Adicionar Imagem
              </button>
              <modal-add-image :visible="addingImage" :variations="product.variations"
                               :callback="uploadedImage" @close="close => addingImage = close"
                               :module="moduleUploadImageModal"/>
            </div>

            <div class="l-row">
              <form-group label="Título" :columns="12" slot="product" :error="getError('title', product.productId)" :required="true">
                <input class="form-input" type="text" v-model="product.title" :disabled="disabledInput('title')">
                <small v-if="maxTitleLengthDI !== null" class="form-help" :class="titleLength">{{ getTitleLengthLabel() }}</small>
              </form-group>

              <form-group label="Descrição" :columns="12" slot="product" :error="getError('description', product.productId)" :required="true">
                <textarea class="form-textarea" rows="8" v-model="product.description" :disabled="disabledInput('description')"/>
                <small v-if="maxDescriptionLengthDI !== null" class="form-help" :class="descriptionLength">{{ getDescriptionLengthLabel() }}</small>
              </form-group>
              <slot/>
            </div>

            <div class="l-row">
              <form-group label="Categoria" :columns="6" :required="true" :disabled="disabledInputs()" :error="getError('selectedCategory',  product.productId)">
                <loader :loading="loadingCategories"/>
                <template v-if="!loadingCategories">
                  <select
                    v-for="(_, idx) in visibleLevels"
                    :key="idx"
                    :value="getSelectedCategoryId(idx)"
                    @change="onCategoryChangeById(idx, $event.target.value)"
                    class="form-select"
                    :disabled="disabledInputs()"
                    style="margin-bottom:4px"
                  >
                    <option :value="null" disabled>Selecione o nível {{ idx + 1 }}</option>
                    <option v-for="cat in categoryOptions(idx)" :key="cat.id" :value="cat.id">
                      {{ cat.name }}
                    </option>
                  </select>
                </template>
              </form-group>

              <form-group v-if="selectedCategory" label="Marca" :columns="6" :required="true" :disabled="disabledInputs()" :error="getError('selectedBrand', product.productId)">
                <loader :loading="loadingBrands"/>
                <div class="select-wrapper" v-if="brandOptions.length">
                  <select v-model="selectedBrand" class="form-select" :disabled="loadingBrands || disabledInputs()">
                    <option :value="null" disabled>Selecione uma marca</option>
                    <option v-for="brand in brandOptions" :key="brand.id" :value="brand">
                      {{ brand.name }}
                    </option>
                  </select>
                </div>
                <input v-else type="text" class="form-input" v-model="selectedBrand" :disabled="loadingBrands || disabledInputs()" placeholder="Digite a marca" />
              </form-group>
            </div>

          </panel>

          <panel v-if="selectedCategory" title="Atributos">
            <loader :loading="loadingAttributes"/>

            <div v-if="!selectedCategory && !loadingAttributes" class="l-row">
              <div class="l-col-12@md">
                <p class="placeholder-msg">Selecione a categoria para exibir os atributos.</p>
              </div>
            </div>

            <div class="l-row" v-else-if="attributes.length">
              <form-group
                v-for="attribute in viewProductAttributes"
                :key="attribute.id"
                :label="attribute.name"
                :required="attribute.isRequired"
                :columns="6"
                :error="getError(String(attribute.id), product.productId)"
              >
                <multiselect
                  v-if="attribute.isMultipleSelection || attribute.isCustomizable"
                  v-model="localAttrValues[attribute.id]"
                  :options="attribute.values || []"
                  track-by="id"
                  label="name"
                  :multiple="attribute.isMultipleSelection || attribute.isCustomizable"
                  :close-on-select="!attribute.isMultipleSelection"
                  :clear-on-select="false"
                  :hide-selected="true"
                  :taggable="attribute.isCustomizable"
                  @tag="val => addCustomTagProduct(attribute, val)"
                  :placeholder="getAttributePlaceholder(attribute)"
                  :disabled="disabledInputs()"
                />

                <select
                  v-else-if="attribute.values && attribute.values.length"
                  v-model="localAttrValues[attribute.id]"
                  class="form-select"
                  :disabled="disabledInputs()"
                >
                  <option :value="null" disabled>{{ getAttributePlaceholder(attribute) }}</option>
                  <option v-for="value in attribute.values" :key="value.id" :value="value">
                    {{ value.name }}
                  </option>
                </select>

                <input
                  v-else
                  type="text"
                  class="form-input"
                  v-model="localAttrValues[attribute.id]"
                  :placeholder="getAttributePlaceholder(attribute)"
                  :disabled="disabledInputs()"
                />
              </form-group>
            </div>

            <div v-else-if="selectedCategory && !loadingAttributes" class="l-row">
              <div class="l-col-12@md">
                <p class="placeholder-msg">Esta categoria não possui atributos configuráveis.</p>
              </div>
            </div>
          </panel>

          <div class="divider">
            <span>Informações das variações do produto</span>
          </div>

          <div class="l-row vertical-flex">
            <div class="l-col-12@md" v-for="variation in product.variations" :key="variation.sku || variation.id">
              <MarketplaceProvideVariationShein
                :variation="variation"
                :from-module="fromModule"
                :to-module="toModule"
                :variation-attributes="viewVariationAttributes"
                :loading-variation-attributes="loadingAttributes"
              />
            </div>
          </div>

        </div>
      </div>

    </div>
  </shared-product-provide>
</template>

<script>
import Panel from '@/components/template/Panel'
import { quillEditor } from 'vue-quill-editor'
import ModalAddImage from '@/components/marketplace/shared/provide/ModalAddImage'
import { mapActions, mapGetters } from 'vuex'
import api from '@/api'
import notifier from '@/notifier'
import FormGroup from '@/components/template/FormGroup'
import Multiselect from 'vue-multiselect'
import Loader from '@/components/template/Loader'
import MarketplaceProvideVariationShein from '@/components/marketplace/shein/provide/Variation'

export default {
  inject: ['maxDescriptionLengthDI', 'moduleDI', 'maxTitleLengthDI'],
  provide: {
    moduleDI: { type: 'marketplace', identifier: 'shein' }
  },
  data: () => ({
    loading: false,
    maxTitleLengthDI: 60,
    maxDescriptionLengthDI: 2000,

    categoryTree: [],
    categoryPath: {},
    loadingCategories: false,
    selectedCategory: null,

    brandOptions: [],
    selectedBrand: null,
    loadingBrands: false,

    attributes: [],
    loadingAttributes: false,

    show: false,
    addingImage: false,
    localAttrValues: Object.create(null)
  }),
  props: {
    account: { type: Object, required: true },
    product: { type: Object, required: true },
    fromModule: { type: Object, required: true },
    toModule: { type: Object, required: true },
    loading: { type: Boolean, default: false },
    disabledInputList: { type: Array, default: () => [] }
  },
  async mounted () {
    this.removeHtml()

    if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
      this.$set(this.product, 'attributes', {})
    }

    this.$parent.loadingProducts(false)
  },
  methods: {
    ...mapActions('errors', ['addError', 'clearErrors']),
    disabledInput (input) {
      if (!this.disabledInputList.length) return false
      return this.disabledInputList.indexOf(input) !== -1
    },
    toggleProductInfo () {
      if (['sent', 'error', 'cannotSend'].includes(this.product.status)) return
      this.show = !this.show
    },
    toggleIconClass () {
      return { 'icon-circle-up': this.show, 'icon-circle-down': !this.show || this.everyVariationWithErrorOrSent }
    },
    removeHtml () {
      const doc = new DOMParser().parseFromString(this.product.description, 'text/html')
      doc.querySelectorAll('li').forEach(li => {
        li.textContent = '  • ' + li.textContent
      })
      let text = ''
      const walker = doc.createTreeWalker(doc.body, 4 | 1)
      let node = walker.nextNode()
      while (node) {
        if (node.nodeType === 3) {
          text += node.textContent
        } else if (node.nodeType === 1 && /^(P|DIV|H[1-6]|LI|BR)$/.test(node.tagName)) {
          text += '\n'
        }
        node = walker.nextNode()
      }
      text = text
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
      this.product.description = text
    },
    getDescriptionLengthLabel () { return `${this.product.description.length}/${this.maxDescriptionLengthDI}` },
    getTitleLengthLabel () { return `${this.product.title.length}/${this.maxTitleLengthDI}` },
    getCountVariations () {
      const selectedVariations = this.product.variations.filter(v => v.selected)
      const sent = selectedVariations.filter(v => v.status !== 'sending').length
      return `${sent}/${selectedVariations.length} variações enviadas`
    },
    addCustomTagProduct (attribute, val) {
      if (!attribute.values) this.$set(attribute, 'values', [])
      const exists = attribute.values.some(v => String(v.id) === String(val))
      if (!exists) attribute.values.push({ id: String(val), name: String(val), custom: true })
      if (attribute.isMultipleSelection || attribute.isCustomizable) {
        const cur = Array.isArray(this.localAttrValues[attribute.id]) ? [...this.localAttrValues[attribute.id]] : []
        if (!cur.some(v => String((v && v.id) || v) === String(val))) {
          cur.push({ id: String(val), name: String(val), custom: true })
        }
        this.$set(this.localAttrValues, attribute.id, cur)
      } else {
        this.$set(this.localAttrValues, attribute.id, { id: String(val), name: String(val), custom: true })
      }
    },
    uploadedImage (urls, variations) {
      for (const v of variations) {
        const found = this.product.variations.find(x => x.sku === v.sku)
        if (!found) continue
        for (const u of urls) found.images.push(u)
      }
      variations.splice(0, variations.length)
    },
    normalizeTreeNode (node) {
      if (!node) return null
      return {
        id: node.category_id || node.id,
        name: node.category_name || node.name,
        product_type_id: node.product_type_id,
        last_category: node.last_category === true,
        children: (node.children || []).map(c => this.normalizeTreeNode(c)).filter(Boolean)
      }
    },
    findCategoryById (idx, id) {
      return (this.categoryOptions(idx) || []).find(c => String(c.id) === String(id)) || null
    },
    getSelectedCategoryId (idx) {
      const cat = this.categoryPath[idx]
      return cat && cat.id != null ? String(cat.id) : null
    },
    resetCategorySelection () {
      this.categoryPath = {}
      this.selectedCategory = null
      this.attributes = []
      this.loadingAttributes = false

      if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
        this.$set(this.product, 'attributes', {})
      } else {
        Object.keys(this.product.attributes).forEach(k => this.$delete(this.product.attributes, k))
      }

      this.localAttrValues = Object.create(null)

      if (Array.isArray(this.product.variations)) {
        this.product.variations.forEach(v => {
          if (!v.attributes) {
            this.$set(v, 'attributes', {})
          }
        })
      }
    },
    onCategoryChangeById (idx, rawId) {
      const id = rawId === 'null' || rawId === '' ? null : rawId
      const cat = id ? this.findCategoryById(idx, id) : null

      const keys = Object.keys(this.categoryPath)
        .map(Number)
        .filter(k => !isNaN(k) && k >= idx)
        .sort()
      keys.forEach(k => this.$delete(this.categoryPath, k))

      if (cat) {
        this.$set(this.categoryPath, idx, cat)
      }

      this.selectedCategory = null
      this.selectedBrand = null
      this.brandOptions = []
      this.attributes = []

      if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
        this.$set(this.product, 'attributes', {})
      } else {
        Object.keys(this.product.attributes).forEach(k => this.$delete(this.product.attributes, k))
      }

      this.localAttrValues = Object.create(null)

      if (cat && (cat.last_category === true || !cat.children || !cat.children.length)) {
        this.selectedCategory = cat
        this.fetchCategoryAttributes()
        this.fetchBrands(cat.product_type_id)
      }
    },
    async fetchCategoryTree () {
      this.loadingCategories = true
      try {
        const raw = await api.shein.getCategories(
          this.account.identifier
        )
        const data = Array.isArray(raw) ? raw : (raw && raw.data ? raw.data : [])
        this.categoryTree = data.map(n => this.normalizeTreeNode(n)).filter(Boolean)
      } catch (_) {
        notifier.error('Erro ao buscar as categorias.')
        this.categoryTree = []
      } finally {
        this.loadingCategories = false
      }
    },
    applyCategoryPreset () {
      const preset = this.$parent.getPresetItemsCategorization(this.product.categories || [])

      if (!preset || !Array.isArray(preset.categories)) return

      preset.categories.forEach((presetCategory, idx) => {
        if (!this.findCategoryById(idx, presetCategory.id)) return
        this.onCategoryChangeById(idx, String(presetCategory.id))
      })
    },
    normalizeAttributeText (text) {
      return String(text || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()
    },
    getVariationSource (variation, keys) {
      const attrs = variation.attributes || {}
      const key = Object.keys(attrs).find(k => keys.includes(this.normalizeAttributeText(k)))
      const value = key !== undefined ? attrs[key] : null
      return typeof value === 'string' && value.trim() ? value.trim() : null
    },
    getColorCandidates (source) {
      const text = this.normalizeAttributeText(source)
      return [text, text.replace(/\s*[/+,-]\s*/g, ' e ')]
    },
    getSizeCandidates (source) {
      const text = this.normalizeAttributeText(source).replace(/\s+/g, '').replace(',', '.')
      const letters = { p: 's', g: 'l', gg: 'xl', xg: 'xl', xgg: 'xxl', xxg: 'xxl' }
      const range = text.match(/^(\d+)[/-](\d+)$/)
      // "37/38" equivale a "37.5" (meio número entre os dois)
      const half = range && Number(range[2]) === Number(range[1]) + 1 ? `${range[1]}.5` : null
      const variants = half ? [half, text] : [text]
      const candidates = []

      // BR sempre tem prioridade sobre os demais
      ;['br', '', 'eur'].forEach(prefix => variants.forEach(variant => candidates.push(`${prefix}${variant}`)))
      if (letters[text]) candidates.push(letters[text])

      return candidates
    },
    findAttributeValue (candidates, values) {
      for (const candidate of candidates) {
        const found = values.find(v => this.normalizeAttributeText(v.name).replace(/\s+/g, '') === candidate.replace(/\s+/g, ''))
        if (found) return found
      }
      return null
    },
    applyVariationAttributes () {
      const variationTypes = [
        { names: ['OPENAPI-Cor', 'Cor', 'cor'], keys: ['color', 'cor'], candidates: this.getColorCandidates },
        { names: ['OPENAPI-Tamanho', 'Tamanho', 'tamanho'], keys: ['size', 'tamanho'], candidates: this.getSizeCandidates }
      ]

      variationTypes.forEach(({ names, keys, candidates }) => {
        const attribute = this.variationAttributes.find(a => names.includes(a.name))

        if (!attribute || !(attribute.values || []).length) return

        const multiple = attribute.isMultipleSelection || attribute.isCustomizable

        this.product.variations.forEach(variation => {
          if (!variation.attributes) this.$set(variation, 'attributes', {})

          const current = variation.attributes[attribute.id]
          if (Array.isArray(current) ? current.length : current) return

          const source = this.getVariationSource(variation, keys)
          if (!source) return

          const value = this.findAttributeValue(candidates(source), attribute.values)
          if (!value) return

          const selected = { id: String(value.id), name: String(value.name) }
          this.$set(variation.attributes, attribute.id, multiple ? [selected] : selected)
        })
      })
    },
    async fetchBrands (productTypeId) {
      if (!productTypeId) return
      this.loadingBrands = true
      try {
        const raw = await api.shein.getBrands(
          this.account.identifier,
          productTypeId
        )
        const data = Array.isArray(raw) ? raw : (raw && raw.data ? raw.data : [])
        this.brandOptions = data.map(b => ({
          id: b.brand_code || b.id,
          name: b.brand_name || b.name
        }))
      } catch (_) {
        notifier.error('Erro ao buscar as marcas.')
        this.brandOptions = []
      } finally {
        this.loadingBrands = false
      }
    },
    async fetchCategoryAttributes () {
      if (!this.selectedCategory) return
      this.loadingAttributes = true

      const productTypeId = this.selectedCategory.product_type_id

      try {
        const raw = await api.shein.getAttributes(
          this.account.identifier,
          productTypeId
        )

        const list = Array.isArray(raw) ? raw : (raw && raw.data ? raw.data : [])

        const variationAttributeNames = ['OPENAPI-Tamanho', 'OPENAPI-Cor', 'Cor', 'cor', 'Tamanho', 'tamanho']

        this.attributes = list.map(a => {
          const mode = a.mode
          const isVariation = variationAttributeNames.includes(a.name)

          return {
            id: a.id,
            name: a.name,
            attrType: a.attrType,
            attribute_type: isVariation ? 1 : 3,
            isMultipleSelection: mode === 'multiSelect',
            isRequired: a.required === true,
            isCustomizable: mode === 'select/custom',
            values: (a.enum || []).map(v => ({
              id: v.id,
              name: v.value || v.name
            }))
          }
        })

        this.initProductAttributeShape()
        this.product.variations.forEach(v => { if (!v.attributes) this.$set(v, 'attributes', {}) })
        this.applyVariationAttributes()
      } catch (_) {
        notifier.error('Erro ao buscar os atributos da categoria selecionada.')
        this.attributes = []
      } finally {
        this.loadingAttributes = false
      }
    },
    initProductAttributeShape () {
      if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
        this.$set(this.product, 'attributes', {})
      }

      this.productOnlyAttributes.forEach(attr => {
        const cur = this.product.attributes[attr.id]

        if (attr.isMultipleSelection || attr.isCustomizable) {
          if (!Array.isArray(cur)) {
            this.$set(this.localAttrValues, attr.id, [])
          } else if (cur.length && typeof cur[0] !== 'object') {
            const mapped = cur
              .map(id => (attr.values || []).find(v => String(v.id) === String(id)))
              .filter(Boolean)
            this.$set(this.localAttrValues, attr.id, mapped)
          } else {
            this.$set(this.localAttrValues, attr.id, [...cur])
          }
        } else {
          if (cur == null) {
            this.$set(this.localAttrValues, attr.id, null)
            return
          }
          if (typeof cur === 'object') {
            this.$set(this.localAttrValues, attr.id, cur)
            return
          }
          const found = (attr.values || []).find(v => String(v.id) === String(cur))
          this.$set(this.localAttrValues, attr.id, found || null)
        }
      })
    },
    getAttributePlaceholder (attribute) {
      if (!attribute) return 'Informe um valor'

      if (attribute.isMultipleSelection) {
        return `Selecione um ou mais valores de ${attribute.name}`
      }

      if (attribute.isCustomizable) {
        return `Selecione ou digite o valor de ${attribute.name}`
      }

      if (attribute.values && attribute.values.length) {
        return `Selecione o valor de ${attribute.name}`
      }

      return `Informe o valor de ${attribute.name}`
    },
    formatProduct () {
      this.clearErrors()

      const pid = this.product.productId

      const addReq = (property, sku) => {
        this.addError({
          productId: pid,
          ...(sku ? { sku } : {}),
          property,
          error: { message: 'required', value: 1 }
        })
      }

      const isNil = v => v === undefined || v === null
      const isBlank = v => isNil(v) || (typeof v === 'string' && v.trim() === '')

      if (!this.account) addReq('account')
      if (!this.selectedCategory) addReq('selectedCategory')
      if (!this.selectedBrand) addReq('selectedBrand')

      if (isBlank(this.product.title)) addReq('title')
      if (isBlank(this.product.description)) addReq('description')

      if (this.maxTitleLengthDI != null && (this.product.title || '').length > this.maxTitleLengthDI) {
        this.addError({ productId: pid, property: 'title', error: { message: 'maxLength', value: this.maxTitleLengthDI } })
      }
      if (this.maxDescriptionLengthDI != null && (this.product.description || '').length > this.maxDescriptionLengthDI) {
        this.addError({ productId: pid, property: 'description', error: { message: 'maxLength', value: this.maxDescriptionLengthDI } })
      }

      const productAttributes = this.productOnlyAttributes
        .map(attr => {
          if (attr.isRequired) {
            if (attr.isMultipleSelection || attr.isCustomizable) {
              const cur = this.localAttrValues[attr.id]
              if (!Array.isArray(cur) || cur.length === 0) addReq(String(attr.id))
            } else {
              if (attr.values && attr.values.length) {
                const cur = this.localAttrValues[attr.id]
                if (!cur || typeof cur !== 'object') addReq(String(attr.id))
              } else {
                const cur = this.localAttrValues[attr.id]
                if (isBlank(cur)) addReq(String(attr.id))
              }
            }
          }

          let valueId = ''
          let valueLabel = ''

          if (attr.isMultipleSelection || attr.isCustomizable) {
            const list = Array.isArray(this.localAttrValues[attr.id]) ? this.localAttrValues[attr.id] : []
            const normalized = list
              .map(o => {
                if (o && typeof o === 'object' && o.id != null) return { id: String(o.id), name: String(o.name ? o.name : o.id) }
                if (o != null) {
                  const found = (attr.values || []).find(v => String(v.id) === String(o))
                  if (found) return { id: String(found.id), name: String(found.name ? found.name : found.id) }
                  return { id: String(o), name: String(o) }
                }
                return null
              })
              .filter(Boolean)

            if (!normalized.length) return null
            valueId = normalized.map(o => o.id)
            valueLabel = normalized.map(o => o.name).join(', ')
          } else if (attr.values && attr.values.length) {
            const obj = this.localAttrValues[attr.id]
            if (obj && obj.id != null) {
              valueId = String(obj.id)
              valueLabel = String(obj.name ? obj.name : obj.id)
            } else {
              return null
            }
          } else {
            const txt = (this.localAttrValues[attr.id] ? this.localAttrValues[attr.id] : '').toString().trim()
            if (!txt) return null
            valueId = txt
            valueLabel = txt
          }

          return {
            id: `${attr.attrType}/${attr.id}`,
            label: String(attr.name),
            valueId,
            valueLabel
          }
        })
        .filter(Boolean)

      const selectedVariations = (this.product.variations || []).filter(v => v && v.selected)

      const attrSelectedSomewhere = new Set(
        this.variationAttributes
          .filter(attr => {
            return selectedVariations.some(variation => {
              const val = (variation.attributes || {})[attr.id]
              if (attr.isMultipleSelection || attr.isCustomizable) {
                return Array.isArray(val) && val.length > 0
              }
              if (attr.values && attr.values.length) {
                return !!(val && typeof val === 'object' && val.id != null)
              }
              return !isBlank(val)
            })
          })
          .map(attr => String(attr.id))
      )

      selectedVariations.forEach(variation => {
        const sku = variation.sku || variation.id
        if (!variation.attributes) variation.attributes = {}

        this.variationAttributes.forEach(attr => {
          const requiredForAll = !!attr.isRequired || attrSelectedSomewhere.has(String(attr.id))
          if (!requiredForAll) return

          const val = variation.attributes[attr.id]

          if (attr.isMultipleSelection || attr.isCustomizable) {
            if (!Array.isArray(val) || val.length === 0) addReq(String(attr.id), sku)
            return
          }

          if (attr.values && attr.values.length) {
            if (!val || typeof val !== 'object' || val.id == null) addReq(String(attr.id), sku)
            return
          }

          if (isBlank(val)) addReq(String(attr.id), sku)
        })
      })

      const normalizeVarAttrs = (variation) => {
        return this.variationAttributes
          .map(attr => {
            const raw = variation.attributes ? variation.attributes[attr.id] : undefined

            let valueId = ''
            let valueLabel = ''

            if (attr.isMultipleSelection || attr.isCustomizable) {
              const list = Array.isArray(raw) ? raw : []
              const normalized = list
                .map(o => {
                  if (o && typeof o === 'object' && o.id != null) {
                    return { id: String(o.id), name: String(o.name ? o.name : o.id) }
                  }
                  if (o != null) {
                    const found = (attr.values || []).find(v => String(v.id) === String(o))
                    return found
                      ? { id: String(found.id), name: String(found.name ? found.name : found.id) }
                      : { id: String(o), name: String(o) }
                  }
                  return null
                })
                .filter(Boolean)

              if (!normalized.length) return null

              valueId = normalized.map(o => o.id)
              valueLabel = normalized.map(o => o.name).join(', ')
            } else if (attr.values && attr.values.length) {
              if (!raw || typeof raw !== 'object' || raw.id == null) return null
              valueLabel = String(raw.name ? raw.name : raw.id)
            } else {
              const txt = (raw == null) ? '' : String(raw).trim()
              if (!txt) return null
              valueId = txt
              valueLabel = txt
            }

            return {
              id: `${attr.attrType}/${attr.id}`,
              label: String(attr.name),
              valueId,
              valueLabel
            }
          })
          .filter(Boolean)
      }

      const shared = {
        productId: pid,
        title: this.product.title,
        description: this.product.description,
        attributes: productAttributes,
        categoryId: this.selectedCategory ? this.selectedCategory.id : null,
        productTypeId: this.selectedCategory ? this.selectedCategory.product_type_id : null,
        brand: this.selectedBrand ? (this.selectedBrand.name || this.selectedBrand) : null,
        brandId: this.selectedBrand ? (this.selectedBrand.id || null) : null,
        account: { identifier: this.account.identifier },
        source: this.fromModule,
        to: this.toModule
      }

      return selectedVariations.map(v => ({
        ...shared,
        sku: v.sku,
        ean: v.ean,
        price: v.price,
        stock: v.stock,
        length: v.length,
        width: v.width,
        height: v.height,
        weight: v.weight,
        images: Array.isArray(v.images) && v.images.length
          ? v.images.map(url => ({ url }))
          : null,
        variationAttributes: normalizeVarAttrs(v),
        original: { product: this.product, variation: v }
      }))
    },
    disabledInputs () {
      return this.product.status === 'sent' || this.product.status === 'error'
    },
    sortAttributes (a, b) {
      if (a.isRequired !== b.isRequired) return a.isRequired ? -1 : 1
      return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR')
    }
  },
  computed: {
    ...mapGetters('errors', ['getError']),
    ...mapGetters('module', ['getDefinitionModuleByIdentifierAndType']),
    moduleDefinition () { return this.getDefinitionModuleByIdentifierAndType('marketplace', 'shein') },
    sending () { return !!this.product.variations.filter(v => v.status === 'sending').length },
    marketplaceVariationProvide () { return 'MarketplaceProvideVariationShein' },
    everyVariationWithErrorOrSent () { return this.product.variations.every(v => ['error', 'sent'].includes(v.status)) },
    descriptionLength () {
      return { 't-success': this.product.description.length <= this.maxDescriptionLengthDI, 't-error': this.product.description.length > this.maxDescriptionLengthDI }
    },
    titleLength () {
      return { 't-success': this.product.title.length <= this.maxTitleLengthDI, 't-error': this.product.title.length > this.maxTitleLengthDI }
    },
    moduleUploadImageModal () { return { type: this.toModule.type, identifier: this.toModule.identifier } },

    visibleLevels () {
      const levels = []
      let i = 0
      while (true) {
        levels.push(i)
        const cat = this.categoryPath[i]
        if (!cat || cat.last_category === true || !cat.children || !cat.children.length) {
          break
        }
        i++
      }
      return levels
    },
    categoryOptions () {
      return (idx) => {
        if (idx === 0) return this.categoryTree
        const parent = this.categoryPath[idx - 1]
        return parent ? parent.children || [] : []
      }
    },

    productOnlyAttributes () {
      return (this.attributes || []).filter(a => a.attribute_type === 3 || a.attribute_type === 4)
    },
    variationAttributes () {
      return (this.attributes || []).filter(a => a.attribute_type === 1 || a.attribute_type === 2)
    },
    viewProductAttributes () {
      return this.productOnlyAttributes.slice().sort((a, b) => this.sortAttributes(a, b))
    },
    viewVariationAttributes () {
      return this.variationAttributes.slice().sort((a, b) => this.sortAttributes(a, b))
    }
  },
  watch: {
    localAttrValues: {
      deep: true,
      handler (val) {
        if (!this.product.attributes || Array.isArray(this.product.attributes) || typeof this.product.attributes !== 'object') {
          this.$set(this.product, 'attributes', {})
        }
        Object.keys(val).forEach(attrId => {
          const cur = val[attrId]
          if (Array.isArray(cur)) {
            this.$set(this.product.attributes, attrId, cur.map(o => ({ ...o })))
          } else if (cur && typeof cur === 'object') {
            this.$set(this.product.attributes, attrId, { ...cur })
          } else {
            this.$set(this.product.attributes, attrId, cur)
          }
        })
      }
    },
    account: {
      async handler (v) {
        if (v && v.identifier) {
          this.resetCategorySelection()
          this.brandOptions = []
          this.selectedBrand = null
          await this.fetchCategoryTree()
          this.applyCategoryPreset()
        }
      },
      immediate: true
    }
  },
  components: {
    FormGroup,
    Multiselect,
    Loader,
    Panel,
    quillEditor,
    ModalAddImage,
    MarketplaceProvideVariationShein
  }
}
</script>

<style lang="sass" scoped>
.c-box
  margin-bottom: 16px
.c-box__body
  background: #d9edf7
.c-box__heading
  cursor: pointer
  height: 50px
  line-height: 2
  font-size: 15px
  color: #31708f
  background-color: #d9edf7
  border-color: #bde2fb
.icon-circle-down, .icon-circle-up
  font-size: 30px
.c-box__body
  border-color: #bde2fb
.divider
  margin-bottom: 16px
.status
  margin-right: 20px
  margin-top: 4px
.disabledHeader
  cursor: not-allowed
  background-color: #E7E7E7
  border-color: #D3D3D3
  .icon
    opacity: 0.3

.placeholder-msg
  color: #666
  margin: 8px 0
</style>



// WEBPACK FOOTER //
// Product.vue?0bef6161